import { useState } from "react";
import { toast } from "sonner";
import { Home } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const HTTP_UNAUTHORIZED = 401;
const HTTP_VALIDATION_ERROR = 422;

const LoginPage = () => {
  const { handleLogin } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg("");

    // Clear any stale tokens before login attempt
    localStorage.removeItem("unity_token");
    localStorage.removeItem("unity_username");

    try {
      const res = await api.login(username, password);
      if (res.data?.token) {
        handleLogin(res.data.token, res.data.username);
        toast.success("Zalogowano pomyslnie");
      } else {
        setErrorMsg("Serwer nie zwrocil tokenu. Sprobuj ponownie.");
      }
    } catch (err) {
      if (err.response?.status === HTTP_UNAUTHORIZED) {
        setErrorMsg("Nieprawidłowy email lub hasło");
      } else if (err.response?.status === HTTP_VALIDATION_ERROR) {
        setErrorMsg("Wprowadź email i hasło");
      } else if (!err.response) {
        setErrorMsg("Brak polaczenia z serwerem. Sprawdz internet i sprobuj ponownie.");
      } else {
        setErrorMsg(`Blad serwera (${err.response.status}). Sprobuj ponownie.`);
      }
      toast.error(errorMsg || "Blad logowania");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center mx-auto mb-4">
            <Home className="w-6 h-6 text-primary-foreground" />
          </div>
          <CardTitle className="text-2xl font-['Outfit']">Unity</CardTitle>
          <CardDescription>Zaloguj się do aplikacji</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Email</Label>
              <Input
                id="username"
                type="email"
                value={username}
                onChange={(e) => { setUsername(e.target.value); setErrorMsg(""); }}
                placeholder="email@example.com"
                required
                data-testid="login-username"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Hasło</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setErrorMsg(""); }}
                placeholder="********"
                required
                data-testid="login-password"
              />
            </div>
            {errorMsg && (
              <div className="text-sm text-red-500 bg-red-500/10 rounded-md p-3" data-testid="login-error">
                {errorMsg}
              </div>
            )}
            <Button type="submit" className="w-full" disabled={isLoading} data-testid="login-submit">
              {isLoading ? "Logowanie..." : "Zaloguj się"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
